import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Vendored, not installed.
 *
 * This is a white-labelled product: every string a user can see has to be
 * auditable in our own repository, and a component pulled from a registry at
 * build time is a string we did not write appearing on a surface we promised to
 * control. So the shadcn shape is kept and the code lives here.
 *
 * ACTION IS INK. The primary control is a black pill — not the accent colour.
 * That is not a stylistic preference: `signal` is the colour of a live run and
 * of a question waiting on the reader, and a blue button standing next to a
 * blue "needs your answer" chip makes both mean less. Ink for the thing you
 * press, colour for the thing that is true.
 *
 * Every control is fully round. Under Soft Pastel the rectangle is reserved for
 * content — a turn, a run block, a question — so shape alone says whether a
 * thing can be pressed.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-graphite-900 text-white hover:bg-graphite-700 disabled:opacity-40',
  secondary:
    'bg-bone-raised text-graphite-900 shadow-[inset_0_0_0_1.5px_var(--rule-strong)] hover:bg-bone disabled:opacity-40',
  ghost: 'bg-transparent text-graphite-500 hover:bg-bone hover:text-graphite-900 disabled:opacity-40',
  danger:
    'bg-transparent text-alarm shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--alarm)_32%,transparent)] hover:bg-alarm-wash disabled:opacity-40',
};

const SIZES: Record<Size, string> = {
  sm: 'h-[34px] px-[15px] text-[0.8125rem] gap-2',
  md: 'h-11 px-[22px] text-[0.90625rem] gap-2',
  lg: 'h-[52px] px-7 text-base gap-2.5',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={props.type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        // Full-round, because every reference surface this direction came from
        // reserves the pill for controls and keeps rectangles for content.
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-full font-semibold tracking-[-0.01em] transition-[background-color,box-shadow,opacity] duration-150',
        'disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="h-[13px] w-[13px] shrink-0 animate-spin rounded-full border-[1.6px] border-current border-r-transparent"
    />
  );
}
